<script setup>
  import { ref, computed, watch } from "vue";
  import { useUserStore } from "@/stores/useUserStore";
  import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";
  
  const props = defineProps({
    userIds: {
      type: Array,
      required: true,
    },
  });
  
  const emit = defineEmits(["close", "refresh"]);
  const store = useUserStore();
  
  const isDeleting = ref(false);
  
  // ✅ contagem atual (store) — pode mudar após deleção/limpeza
  const selectedCount = computed(() => store.selectedUserIds.length);
  
  // ✅ Congela contagem e IDs (alvo real do delete)
  const frozenCount = ref(0);
  const frozenIds = ref([]);
  
  function freezeSnapshot() {
    const idsFromProps = Array.isArray(props.userIds) ? props.userIds : [];
    const idsFromStore = Array.isArray(store.selectedUserIds) ? store.selectedUserIds : [];
  
    // ✅ Prioriza props.userIds (porque seu modal já recebe isso)
    const ids = idsFromProps.length ? idsFromProps : idsFromStore;
  
    frozenIds.value = [...ids];
    frozenCount.value = frozenIds.value.length;
  }
  
  // ✅ Mantém snapshot atualizado ENQUANTO NÃO está deletando
  watch(
    () => [props.userIds, store.selectedUserIds],
    () => {
      if (!isDeleting.value) freezeSnapshot();
    },
    { immediate: true, deep: true }
  );
  
  const modalTitle = "Apagar Usuários em Lote";
  
  const messageTitle = computed(() => {
    const n = frozenCount.value || selectedCount.value;
  
    if (n <= 1) return "Você está prestes a apagar 1 usuário selecionado.";
    return `Você está prestes a apagar ${n} usuários selecionados.`;
  });
  
  const messageSubtitle = computed(() => "Esta ação não pode ser desfeita.");
  
  function buildErrorMessage(error) {
    return (
      error?.response?.data?.message ||
      error?.message ||
      "Ocorreu um erro ao excluir os usuários."
    );
  }
  
  async function onConfirmDelete() {
    // ✅ congela ANTES de deletar (texto + alvo)
    freezeSnapshot();
    isDeleting.value = true;
  
    try {
      if (!frozenIds.value.length) {
        throw new Error("Nenhum usuário selecionado para exclusão.");
      }
  
      // ✅ garante que o store delete exatamente o snapshot (sem risco de seleção mudar)
      store.selectedUserIds = [...frozenIds.value];
  
      await store.deleteSelectedUsers();
    } catch (error) {
      isDeleting.value = false;
      throw new Error(buildErrorMessage(error)); // toast vem do DeleteConfirmModal
    }
  }
  
  function handleSuccess() {
    emit("refresh");
    emit("close");
  }
  </script>
  
  <template>
    <DeleteConfirmModal
      :show="true"
      :title="modalTitle"
      :messageTitle="messageTitle"
      :messageSubtitle="messageSubtitle"
      confirmText="Sim, apagar"
      cancelText="Cancelar"
      loadingText="Apagando..."
      :onConfirm="onConfirmDelete"
      @success="handleSuccess"
      @close="!isDeleting && emit('close')"
    />
  </template>
  